import {
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Toolbar,
  ToolbarButton,
} from '@fluentui/react-components';
import { Dismiss16Regular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';

import type { DataFile } from '../../../../../../services/dataHubService.interface.js';
import {
  canLoadDataFilePreview,
  detectDataFileParser,
} from '../../utils/detectDataFileParser.js';
import { parseConversation } from '../../utils/parseConversation.js';
import type { ConversationDocument } from '../../utils/parseConversation.js';
import { ConversationViewer } from '../ConversationViewer/ConversationViewer.js';

import { useStyles } from './DataFilePreview.style.js';

export interface DataFilePreviewProps {
  file: DataFile;
  loadSource: (file: DataFile, signal: AbortSignal) => Promise<string>;
  onClose: () => void;
}

type PreviewState =
  | { status: 'loading' }
  | { status: 'unsupported' }
  | { status: 'error'; message: string }
  | { status: 'ready'; conversation: ConversationDocument };

export function DataFilePreview({
  file,
  loadSource,
  onClose,
}: DataFilePreviewProps) {
  const styles = useStyles();
  const [state, setState] = useState<PreviewState>({ status: 'loading' });
  const fileLabel = file.fileName ?? file.id;

  useEffect(() => {
    if (!canLoadDataFilePreview(file)) {
      setState({ status: 'unsupported' });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState({ status: 'loading' });

    async function loadPreview() {
      try {
        const source = await loadSource(file, controller.signal);
        const parser = detectDataFileParser(source, file);
        if (cancelled) {
          return;
        }

        if (parser === null) {
          setState({ status: 'unsupported' });
          return;
        }

        setState({
          status: 'ready',
          conversation: parseConversation(source, file.fileName),
        });
      } catch (caughtError) {
        if (!cancelled) {
          setState({
            status: 'error',
            message:
              caughtError instanceof Error
                ? caughtError.message
                : 'The file preview could not be loaded.',
          });
        }
      }
    }

    void loadPreview();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [file, loadSource]);

  let content;
  if (state.status === 'loading') {
    content = <Spinner size="small" label={`Loading ${fileLabel}`} />;
  } else if (state.status === 'unsupported') {
    content = (
      <MessageBar intent="info" role="status">
        <MessageBarBody>
          No parsed preview is available for {fileLabel}. You can still download
          the original file.
        </MessageBarBody>
      </MessageBar>
    );
  } else if (state.status === 'error') {
    content = (
      <MessageBar intent="error" role="alert">
        <MessageBarBody>{state.message}</MessageBarBody>
      </MessageBar>
    );
  } else {
    content = (
      <ConversationViewer conversation={state.conversation} isScrollable />
    );
  }

  return (
    <>
      <Toolbar className={styles.toolbar} aria-label="File preview controls">
        <Text weight="semibold">Preview: {fileLabel}</Text>
        <ToolbarButton
          aria-label={`Close preview for ${fileLabel}`}
          icon={<Dismiss16Regular />}
          onClick={onClose}
        />
      </Toolbar>
      {content}
    </>
  );
}
